// Line — 真實資料版（Route 12, PR #102）。
//
// LINE 群組綁定來自 nexus_ops_s00.line_integrations (binding_kind='group')，
// 規則 chips 由 notification_rules.trigger_kind/severity 投影。
// 新增綁定的方式：把 OA 加入 LINE 群組 → webhook 自動寫入。

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MessageSquare, RefreshCw, ServerCrash, Inbox, Building2, Trash2, Pencil, Info, Link2, AlertTriangle, Bell, Plus } from "lucide-react";
import PageHeader from "../components/PageHeader.js";
import DataTable, { type Column } from "../components/DataTable.js";
import EmptyState from "../components/EmptyState.js";
import TenantPickerEmptyState from "../components/TenantPickerEmptyState.js";
import Skeleton from "../components/Skeleton.js";
import Drawer from "../components/Drawer.js";
import { Field } from "../components/Field.js";
import {
  listLineGroups, patchLineGroup, disableLineGroup, friendlyLineError,
  listPendingLineGroups, claimLineGroup,
  type LineGroupRow, type LineGroupStatus, type PendingLineGroupRow,
} from "../lib/lineApi.js";
import {
  listNotificationRules, createNotificationRule, patchNotificationRule,
  disableNotificationRule, friendlyRulesError,
  type NotificationRuleRow, type TriggerKind, type RecipientRole, type RuleStatus,
} from "../lib/notificationRulesApi.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";
import { useConfirmDialog } from "../components/ConfirmDialog.js";

const STATUS_BADGE: Record<string, string> = { active: "badge-success", disabled: "badge-default", muted: "badge-default" };
const STATUS_LABEL: Record<string, string> = { active: "啟用", disabled: "停用", muted: "靜音" };

const TRIGGER_LABEL: Record<TriggerKind, string> = {
  overdue: "巡邏逾期",
  incident: "事件通報",
  daily: "每日摘要",
  handoff: "班表交接",
  system: "系統通知",
  coverage_gap: "排班覆蓋缺口",
  schedule_change: "班表變動",
  submission_response: "管理員回覆任務",
};

const ROLE_LABEL: Record<RecipientRole, string> = {
  owner:          "擁有者",
  manager:        "案場主管",
  officer:        "巡邏員",
  tenant_admin:   "租戶管理員",
  platform_admin: "平台管理員",
  incident_reporter: "通報員",
  auditor:        "稽核員",
};

const SEVERITY_LABEL: Record<string, string> = {
  info: "資訊", low: "低", med: "中", high: "高", critical: "緊急",
};

type ListState =
  | { status: "loading" }
  | { status: "ready"; rows: LineGroupRow[] }
  | { status: "error"; message: string };
type DrawerState = null | { row: LineGroupRow };
type RuleDrawerState = null | { mode: "new" } | { mode: "edit"; rule: NotificationRuleRow };

export default function Line(): React.ReactElement {
  const session = useDashboardSession();
  const tenantParam = session.super_admin ? session.acting_tenant_id ?? null : null;
  const canWrite = session.scopes.includes("write:notification") || session.super_admin === true;
  const [state, setState] = useState<ListState>({ status: "loading" });
  const [editing, setEditing] = useState<DrawerState>(null);
  /** Pending groups visible to write:notification holders. Loaded
   *  alongside the bound list so the page can show "您有 N 個未認領的
   *  群組" up front. */
  const [pending, setPending] = useState<PendingLineGroupRow[]>([]);
  const [claimingId, setClaimingId] = useState<string | null>(null);
  const [claimError, setClaimError] = useState<string | null>(null);
  /** Notification rules — list + drawer state for create/edit. */
  const [rules, setRules] = useState<NotificationRuleRow[]>([]);
  const [rulesError, setRulesError] = useState<string | null>(null);
  const [ruleDrawer, setRuleDrawer] = useState<RuleDrawerState>(null);

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const [bound, pend, rulesResp] = await Promise.all([
        listLineGroups(tenantParam),
        canWrite ? listPendingLineGroups(tenantParam).catch(() => ({ items: [] as PendingLineGroupRow[], correlation_id: null }))
                 : Promise.resolve({ items: [] as PendingLineGroupRow[], correlation_id: null }),
        listNotificationRules({ tenantId: tenantParam, channelKind: "line" })
          .catch(() => ({ items: [] as NotificationRuleRow[], correlation_id: null })),
      ]);
      setState({ status: "ready", rows: bound.items });
      setPending(pend.items);
      setRules(rulesResp.items);
    } catch (err) {
      setState({ status: "error", message: friendlyLineError(err) });
    }
  }, [tenantParam, canWrite]);
  useEffect(() => { void load(); }, [load]);

  const claim = async (id: string): Promise<void> => {
    setClaimingId(id);
    setClaimError(null);
    try {
      await claimLineGroup(id, tenantParam);
      await load();
    } catch (e) {
      setClaimError(friendlyLineError(e));
    } finally {
      setClaimingId(null);
    }
  };

  const columns: Column<LineGroupRow>[] = useMemo(() => [
    {
      key: "name", header: "群組", primary: true, width: "30%",
      render: (g) => (
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-text-muted flex-shrink-0" />
          <div>
            <div className="font-medium text-text-primary">{g.display_name ?? g.group_id ?? "未命名"}</div>
            <div className="text-2xs text-text-muted font-mono">{g.group_id ?? g.id}</div>
          </div>
        </div>
      ),
    },
    {
      key: "rules", header: "通知規則",
      render: (g) => (
        <div className="flex flex-wrap gap-1">
          {g.rules.length === 0 && <span className="text-2xs text-text-muted">尚未設定</span>}
          {g.rules.map((r) => (<span key={r} className="badge-default font-mono text-2xs">{r}</span>))}
        </div>
      ),
    },
    {
      key: "status", header: "狀態",
      render: (g) => <span className={STATUS_BADGE[g.status] ?? "badge-default"}>{STATUS_LABEL[g.status] ?? g.status}</span>,
    },
  ], []);

  if (session.super_admin && !tenantParam) {
    return (<><PageHeader title="LINE 設定" description="LINE 群組綁定與通知規則" />
      <TenantPickerEmptyState /></>);
  }

  return (
    <>
      <PageHeader
        title="LINE 設定"
        description="管理 LINE 群組綁定與通知規則"
        actions={
          <button className="btn-secondary text-sm" onClick={() => void load()} disabled={state.status === "loading"}>
            <RefreshCw className={`h-4 w-4 ${state.status === "loading" ? "animate-spin" : ""}`} /> 重新整理
          </button>
        }
      />
      <div className="rounded-lg border border-accent/30 bg-accent/5 p-3 text-xs text-text-secondary mb-4 flex items-start gap-2">
        <Info className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
        <p>新增 LINE 群組綁定的方式：將 SENTRY 官方帳號加入您的 LINE 群組，系統會自動建立綁定並出現在這個列表。若邀請者已在後台登入，會自動綁到所屬單位；若未登入或屬於多個單位，群組會出現在下方「未綁定群組」清單，由管理員手動認領。</p>
      </div>

      {pending.length > 0 && canWrite && (
        <div className="mb-4 rounded-lg border border-warning/40 bg-warning/5 p-3">
          <div className="flex items-start gap-2 mb-3">
            <AlertTriangle className="h-4 w-4 text-warning mt-0.5 flex-shrink-0" />
            <div className="text-xs text-text-secondary">
              <p className="font-medium text-warning mb-1">未綁定到任何單位的 LINE 群組（{pending.length}）</p>
              <p>下列群組已將 SENTRY 官方帳號加入，但系統無法自動判斷它應該屬於哪個單位。請確認是您的群組後，點「認領到本單位」綁定。</p>
            </div>
          </div>
          <ul className="space-y-2">
            {pending.map((p) => (
              <li key={p.id} className="flex items-center gap-2 rounded-lg bg-bg-raised border border-border-subtle px-3 py-2">
                <MessageSquare className="h-3.5 w-3.5 text-text-muted flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-text-primary truncate">
                    {p.display_name ?? p.group_id ?? "未命名群組"}
                  </div>
                  <div className="text-2xs text-text-muted font-mono truncate">
                    {p.binding_kind === "room" ? "房間" : "群組"} · {p.group_id ?? p.id}
                  </div>
                </div>
                <button
                  onClick={() => void claim(p.id)}
                  disabled={claimingId === p.id}
                  className="btn-secondary text-xs whitespace-nowrap"
                >
                  <Link2 className="h-3.5 w-3.5" />
                  {claimingId === p.id ? "認領中…" : "認領到本單位"}
                </button>
              </li>
            ))}
          </ul>
          {claimError && (
            <div className="mt-2 rounded border border-danger/40 bg-danger/10 p-2 text-xs text-danger">{claimError}</div>
          )}
        </div>
      )}
      {state.status === "loading" && <ListSkeleton />}
      {state.status === "error" && <EmptyState icon={ServerCrash} title="LINE 群組載入失敗" description={state.message}
        action={<button className="btn-primary text-sm" onClick={() => void load()}>再試一次</button>} />}
      {state.status === "ready" && state.rows.length === 0 && (
        <EmptyState icon={MessageSquare} title="這個單位還沒有 LINE 群組綁定" description="請先把 SENTRY 官方帳號加進您要接收通知的 LINE 群組。" />
      )}
      {state.status === "ready" && state.rows.length > 0 && (
        <DataTable rows={state.rows} columns={columns} rowKey={(g) => g.id}
          searchableText={(g) => `${g.display_name ?? ""} ${g.group_id ?? ""}`}
          onRowClick={canWrite ? (g) => setEditing({ row: g }) : undefined}
        />
      )}

      {/* ── 通知規則編輯 ──────────────────────────────────────────────── */}
      <div className="mt-8">
        <div className="flex items-baseline justify-between mb-3">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-text-muted" />
            <h2 className="text-sm font-medium text-text-primary">通知規則</h2>
            <span className="text-2xs text-text-muted">{rules.length} 條</span>
          </div>
          {canWrite && (
            <button onClick={() => setRuleDrawer({ mode: "new" })} className="btn-primary text-xs">
              <Plus className="h-3.5 w-3.5" /> 新增規則
            </button>
          )}
        </div>
        <p className="text-2xs text-text-muted mb-3 leading-relaxed">
          設定哪一種事件（逾期 / 事件通報 / 每日摘要等）由哪個角色接收，以及對應的 Flex 樣板與升級時效。沒設規則時，dispatcher 沿用內建預設。
        </p>
        {rules.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-bg-raised/40 p-4 text-xs text-text-muted text-center">
            尚未設定任何通知規則。{canWrite ? "點右上「新增規則」開始。" : ""}
          </div>
        ) : (
          <ul className="space-y-2">
            {rules.map((r) => (
              <li
                key={r.id}
                className="rounded-lg border border-border bg-bg-raised px-3 py-2 flex items-center gap-3 cursor-pointer hover:bg-bg-hover"
                onClick={() => canWrite && setRuleDrawer({ mode: "edit", rule: r })}
              >
                <span className="badge-default text-2xs font-mono">{TRIGGER_LABEL[r.trigger_kind as TriggerKind] ?? r.trigger_kind}</span>
                <div className="flex-1 min-w-0 text-2xs text-text-secondary">
                  <span className="font-medium text-text-primary">
                    {r.recipient_role ? (ROLE_LABEL[r.recipient_role as RecipientRole] ?? r.recipient_role) : "未指定角色"}
                  </span>
                  {(() => {
                    let cfg: Record<string, unknown> = {};
                    try { cfg = JSON.parse(r.config_json) as Record<string, unknown>; } catch {}
                    const sev = typeof cfg.severity === "string" ? cfg.severity : null;
                    return sev ? <span className="ml-2">嚴重度 {SEVERITY_LABEL[sev] ?? sev}+</span> : null;
                  })()}
                  {r.template && <span className="ml-2 font-mono">樣板 {r.template}</span>}
                  {r.escalate_after_seconds != null && (
                    <span className="ml-2">{Math.round(r.escalate_after_seconds / 60)} 分鐘後升級</span>
                  )}
                </div>
                <span className={`text-2xs ${STATUS_BADGE[r.status] ?? "badge-default"}`}>{STATUS_LABEL[r.status] ?? r.status}</span>
              </li>
            ))}
          </ul>
        )}
        {rulesError && (
          <div className="mt-2 rounded border border-danger/40 bg-danger/10 p-2 text-xs text-danger">{rulesError}</div>
        )}
      </div>

      <Drawer
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing ? `編輯「${editing.row.display_name ?? editing.row.group_id ?? "群組"}」` : "編輯 LINE 群組"}
        description="變更顯示名稱或啟停此群組接收通知"
      >
        {editing && (
          <Form
            row={editing.row} tenantId={tenantParam}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); void load(); }}
            canArchive={canWrite}
          />
        )}
      </Drawer>

      <Drawer
        open={!!ruleDrawer}
        onClose={() => setRuleDrawer(null)}
        title={ruleDrawer?.mode === "edit"
          ? `編輯規則「${TRIGGER_LABEL[ruleDrawer.rule.trigger_kind as TriggerKind] ?? ruleDrawer.rule.trigger_kind}」`
          : "新增通知規則"}
        description="設定哪個事件由哪個角色接收，以及樣板 / 升級時效"
      >
        {ruleDrawer && (
          <RuleForm
            mode={ruleDrawer.mode}
            rule={ruleDrawer.mode === "edit" ? ruleDrawer.rule : undefined}
            tenantId={tenantParam}
            onClose={() => setRuleDrawer(null)}
            onSaved={() => { setRuleDrawer(null); setRulesError(null); void load(); }}
            onError={(m) => setRulesError(m)}
            canArchive={canWrite}
          />
        )}
      </Drawer>
    </>
  );
}

function Form({ row, tenantId, onClose, onSaved, canArchive }: {
  row: LineGroupRow; tenantId: string | null;
  onClose: () => void; onSaved: () => void; canArchive: boolean;
}): React.ReactElement {
  const [name, setName] = useState(row.display_name ?? "");
  const [status, setStatus] = useState<LineGroupStatus>((row.status as LineGroupStatus) ?? "active");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true); setErr(null);
    try { await patchLineGroup(row.id, { display_name: name.trim(), status }, tenantId); onSaved(); }
    catch (e) { setErr(friendlyLineError(e)); } finally { setBusy(false); }
  };
  const { confirm } = useConfirmDialog();
  const remove = async () => {
    if (!(await confirm(`確定要停用「${row.display_name ?? row.group_id}」？`, { destructive: true, okLabel: "停用" }))) return;
    setBusy(true); setErr(null);
    try { await disableLineGroup(row.id, tenantId); onSaved(); }
    catch (e) { setErr(friendlyLineError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <Field label="LINE 群組 ID"><input className="input font-mono" disabled value={row.group_id ?? ""} /></Field>
      <Field label="顯示名稱" hint="只在後台顯示，不會影響 LINE 上的群組名稱">
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="狀態">
        <select className="select" value={status} onChange={(e) => setStatus(e.target.value as LineGroupStatus)}>
          <option value="active">啟用（會收到通知）</option>
          <option value="disabled">停用（暫停通知）</option>
        </select>
      </Field>
      <Field label="目前對應的通知規則">
        <div className="rounded border border-border bg-bg-raised p-3 flex flex-wrap gap-1">
          {row.rules.length === 0 && <span className="text-2xs text-text-muted">尚未設定</span>}
          {row.rules.map((r) => (<span key={r} className="badge-default font-mono text-2xs">{r}</span>))}
        </div>
      </Field>
      {err && <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{err}</div>}
      <div className="flex flex-row items-center justify-between gap-2">
        <div>{canArchive && (<button className="btn-danger text-sm" onClick={() => void remove()} disabled={busy}><Trash2 className="h-4 w-4" /> 停用</button>)}</div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary text-sm" disabled={busy}>取消</button>
          <button onClick={() => void save()} className="btn-primary text-sm" disabled={busy}>{busy ? "儲存中…" : "儲存"}</button>
        </div>
      </div>
    </div>
  );
}

function RuleForm({ mode, rule, tenantId, onClose, onSaved, onError, canArchive }: {
  mode: "new" | "edit";
  rule?: NotificationRuleRow;
  tenantId: string | null;
  onClose: () => void;
  onSaved: () => void;
  onError: (msg: string) => void;
  canArchive: boolean;
}): React.ReactElement {
  // Initial config_json (severity / mute_until) parsed once for editing.
  const initialCfg = useMemo<Record<string, unknown>>(() => {
    if (!rule?.config_json) return {};
    try { return JSON.parse(rule.config_json) as Record<string, unknown>; } catch { return {}; }
  }, [rule]);

  const [triggerKind, setTriggerKind] = useState<TriggerKind>(
    (rule?.trigger_kind as TriggerKind) ?? "incident",
  );
  const [recipientRole, setRecipientRole] = useState<RecipientRole | "">(
    (rule?.recipient_role as RecipientRole) ?? "manager",
  );
  const [severity, setSeverity] = useState<string>(
    typeof initialCfg.severity === "string" ? initialCfg.severity : "med",
  );
  const [template, setTemplate] = useState<string>(rule?.template ?? "");
  const [escalateMin, setEscalateMin] = useState<string>(
    rule?.escalate_after_seconds != null ? String(Math.round(rule.escalate_after_seconds / 60)) : "",
  );
  const [status, setStatus] = useState<RuleStatus>((rule?.status as RuleStatus) ?? "active");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { confirm } = useConfirmDialog();

  const save = async (): Promise<void> => {
    setBusy(true); setErr(null);
    const escSec = escalateMin.trim() ? Math.max(0, Math.min(86400, Math.round(Number(escalateMin) * 60))) : null;
    if (escalateMin.trim() && (!Number.isFinite(Number(escalateMin)) || Number(escalateMin) < 0)) {
      setErr("升級時效需為非負整數（分鐘）。");
      setBusy(false); return;
    }
    const config_json: Record<string, unknown> = { ...initialCfg };
    // Severity threshold is only meaningful for incident-style rules;
    // omit it from other kinds so config_json stays scoped to the
    // semantics that actually apply.
    if (triggerKind === "incident") config_json.severity = severity;
    else delete config_json.severity;
    try {
      if (mode === "new") {
        await createNotificationRule({
          trigger_kind: triggerKind,
          channel_kind: "line",
          recipient_role: recipientRole || null,
          template: template.trim() || null,
          escalate_after_seconds: escSec,
          config_json,
          status,
        }, tenantId);
      } else if (rule) {
        await patchNotificationRule(rule.id, {
          trigger_kind: triggerKind,
          recipient_role: recipientRole || null,
          template: template.trim() || null,
          escalate_after_seconds: escSec,
          config_json,
          status,
        }, tenantId);
      }
      onSaved();
    } catch (e) {
      const msg = friendlyRulesError(e);
      setErr(msg); onError(msg);
    } finally { setBusy(false); }
  };

  const archive = async (): Promise<void> => {
    if (!rule) return;
    if (!(await confirm("確定要停用此通知規則？", { destructive: true, okLabel: "停用" }))) return;
    setBusy(true); setErr(null);
    try { await disableNotificationRule(rule.id, tenantId); onSaved(); }
    catch (e) { const msg = friendlyRulesError(e); setErr(msg); onError(msg); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Field label="事件種類" required>
        <select className="select" value={triggerKind} onChange={(e) => setTriggerKind(e.target.value as TriggerKind)}>
          {(Object.keys(TRIGGER_LABEL) as TriggerKind[]).map((k) => (
            <option key={k} value={k}>{TRIGGER_LABEL[k]}</option>
          ))}
        </select>
      </Field>
      <Field label="接收角色" hint="不指定 = dispatcher 沿用內建預設">
        <select className="select" value={recipientRole} onChange={(e) => setRecipientRole(e.target.value as RecipientRole | "")}>
          <option value="">— 不指定 —</option>
          {(Object.keys(ROLE_LABEL) as RecipientRole[]).map((r) => (
            <option key={r} value={r}>{ROLE_LABEL[r]}</option>
          ))}
        </select>
      </Field>
      {triggerKind === "incident" && (
        <Field label="嚴重度門檻" hint="此門檻以上的事件才會推送（含等於）">
          <select className="select" value={severity} onChange={(e) => setSeverity(e.target.value)}>
            {Object.entries(SEVERITY_LABEL).map(([v, l]) => (
              <option key={v} value={v}>{l}+</option>
            ))}
          </select>
        </Field>
      )}
      <Field label="Flex 樣板名稱" hint="進階：對應 packages/shared/src/line/flex 的 builder。留空使用預設樣板。">
        <input className="input font-mono" value={template} onChange={(e) => setTemplate(e.target.value)}
               placeholder="例如：incident-report / overdue-alert / daily-summary" />
      </Field>
      <Field label="升級時效（分鐘）" hint="未在此時間內被確認則升級到上層角色。留空＝不升級。">
        <input className="input font-mono" type="number" min={0} max={1440}
               value={escalateMin} onChange={(e) => setEscalateMin(e.target.value)} placeholder="例如：15" />
      </Field>
      <Field label="狀態">
        <select className="select" value={status} onChange={(e) => setStatus(e.target.value as RuleStatus)}>
          <option value="active">啟用</option>
          <option value="muted">靜音</option>
          <option value="disabled">停用</option>
        </select>
      </Field>
      {err && <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{err}</div>}
      <div className="flex flex-row items-center justify-between gap-2">
        <div>
          {mode === "edit" && rule && canArchive && (
            <button className="btn-danger text-sm" onClick={() => void archive()} disabled={busy}>
              <Trash2 className="h-4 w-4" /> 停用
            </button>
          )}
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary text-sm" disabled={busy}>取消</button>
          <button onClick={() => void save()} className="btn-primary text-sm" disabled={busy}>{busy ? "儲存中…" : "儲存"}</button>
        </div>
      </div>
    </div>
  );
}

function ListSkeleton(): React.ReactElement {
  return (
    <div className="card overflow-hidden">
      <div className="p-4 border-b border-border"><Skeleton className="h-9 w-72" /></div>
      <div className="divide-y divide-border">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center gap-4 p-4">
            <Skeleton className="h-4 w-4 rounded" />
            <div className="flex-1 space-y-1.5"><Skeleton className="h-4 w-1/3" /><Skeleton className="h-3 w-1/2" /></div>
            <Skeleton className="h-5 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}
